import { useCallback, useEffect, useMemo, useState } from 'react';
import useAxios from '../../../app/hooks/useAxios';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import useDocumentFiles from './useDocumentFiles';
import fileDownload from 'js-file-download';
import IDocumentFile from '../interfaces/IDocumentFile';
import useAppLog from '../../../app/features/log/hooks/useAppLog';

const useDocumentFile = (documentId: number | null) => {
	const { documents } = useDocumentFiles();
	const auth = useAuthContext();
	const appLog = useAppLog();
	const { get } = useAxios();
	const [currentDocument, setCurrentDocument] = useState<IDocumentFile | null>(null);
	useEffect(() => {
		if (documentId && documents) {
			const existDocument = documents.find((x) => x.id === documentId);

			if (existDocument) {
				setCurrentDocument(existDocument);
			} else {
				setCurrentDocument(null);
			}
		} else if (documentId === null) {
			setCurrentDocument(null);
		}
	}, [documentId, documents]);

	const getBlob = useCallback(() => {
		return new Promise((resolve, reject) => {
			if (currentDocument && appLog) {
				return get(
					auth.authOverrideUserId
						? `/download/document/${currentDocument.id.toString()}?userId=${auth.authOverrideUserId}`
						: `/download/document/${currentDocument.id.toString()}`,
					{
						responseType: 'blob',
					},
				)
					.then((response) => {
						resolve(response.data);
					})
					.catch((e) => {
						appLog.exception(e, {
							component: 'useDocument.getBlob',
						});
						reject('404');
					});
			} else {
				appLog.error('no_current_document', {
					component: 'useDocument.getBlob',
				});
				reject('no_current_document');
			}
		});
	}, [appLog, auth.authOverrideUserId, currentDocument, get]);

	const download = useCallback(() => {
		return new Promise((resolve, reject) => {
			if (currentDocument && appLog) {
				return get(
					auth.authOverrideUserId
						? `/download/document/${currentDocument.id.toString()}?userId=${auth.authOverrideUserId}`
						: `/download/document/${currentDocument.id.toString()}`,
					{
						responseType: 'blob',
					},
				)
					.then((response) => {
						fileDownload(response.data, currentDocument.name);
						resolve(true);
					})
					.catch((e) => {
						appLog.exception(e, {
							component: 'useDocument',
						});
						reject('404');
					});
			} else {
				appLog.error('no_current_document', {
					component: 'useDocument',
				});
				reject('no_current_document');
			}
		});
	}, [appLog, auth.authOverrideUserId, currentDocument, get]);

	const result = useMemo(() => {
		return {
			document: currentDocument,
			download,
			getBlob,
		};
	}, [currentDocument, download, getBlob]);

	return result;
};

export default useDocumentFile;
